// /moodboard-builder — DEV-ONLY style-reference curation harness (Jun 15).
//
// Workflow:
//   1. Drop / paste (⌘V) / upload an inspiration image (Pinterest screenshot,
//      real ad, brand campaign reference).
//   2. POST /api/dev/moodboard/analyze → gpt-4.1-mini vision classifies into
//      one of the 5 _CD_VIBE buckets and suggests tags / palette / summary.
//   3. Edit the vibe + tags + summary inline if you disagree with the model.
//   4. Save → backend downscales to 1024² WebP, uploads to GCS bucket
//      `socialposts-ai-prod-moodboards`, writes a sidecar JSON, returns the
//      new record. The page drops it into the grid below without a reload.
//   5. Delete (hover an existing tile) → DELETE /api/dev/moodboard/:slug/:id.
//
// Dev-only: gated on import.meta.env.DEV. Costs ~$0.001 per analyze (vision)
// and $0 to save (GCS upload only — no LLM).

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { API_BASE, getAuthHeaders } from '@/lib/api';

const IS_DEV = import.meta.env.DEV;

// Must match _MOODBOARD_VIBES in backend/routes/dev.py exactly.
type Vibe =
  | 'Premium'
  | 'Warm & lifestyle'
  | 'Playful'
  | 'Clean & minimal'
  | 'Editorial';

const VIBES: Vibe[] = [
  'Premium',
  'Warm & lifestyle',
  'Playful',
  'Clean & minimal',
  'Editorial',
];

// Mirrors backend's _vibe_to_slug — lowercase, &→and, spaces→hyphen.
function vibeToSlug(vibe: string): string {
  return vibe
    .toLowerCase()
    .replace(/&/g, 'and')
    .replace(/\s+/g, '-')
    .replace(/,/g, '')
    .replace(/^-+|-+$/g, '');
}

type AnalyzeResult = {
  vibe: Vibe;
  confidence: number;
  style_summary: string;
  palette_words: string;
  composition_notes: string;
  suggested_tags: string[];
};

type MoodboardRecord = {
  id: string;
  vibe: Vibe;
  vibe_slug: string;
  tags: string[];
  style_summary: string;
  palette_words: string;
  composition_notes: string;
  image_path: string;
  image_url: string;
  // Baked preview — the rendered ad produced FROM this recipe (approve flow).
  // Shown next to the source image when the "Show previews" toggle is on, to
  // eyeball how closely the render matches the recipe.
  preview_url?: string;
  preview_path?: string;
  created_at: string;
  // ─── Approval metadata (Jun 16 sidecar enrichment) ─────────────────
  // Set by the smoke-test approve flow. Production worker filters on
  // ready_for_production=true; this page surfaces it as a badge.
  ready_for_production?: boolean;
  approved_at?: string;
  approval_id?: string;
  approved_combo?: {
    vision_model?: string;
    render_model?: string;
    ref_mode?: string;
  };
  approved_notes?: string;
  // ─── Rejection metadata (Jun 16) ───────────────────────────────────
  // Set by the reject button on this page. Mutually exclusive with
  // ready_for_production — rejecting also strips approval flags
  // server-side so a rejected moodboard can't be picked by production.
  rejected?: boolean;
  rejected_at?: string;
  // ─── Product type tagging (Jun 17) ─────────────────────────────────
  // Multi-select — picker filters approved recipes against the active
  // brand's product category. Empty/missing = treated as universal for
  // back-compat.
  product_types?: ProductType[];
  // ─── Recipe content (Jun 17 evening v3) ────────────────────────────
  // The approved vision spec — populated when ready_for_production=true.
  // Edited via the recipe editor modal; consumed by the brand-setup
  // render pipeline at runtime. Shape is open-ended (matches whatever
  // the vision model returned + any palette harmonization).
  approved_recipe?: Record<string, unknown>;
  // ─── Recipe versioning (Jun 17 evening v4) ─────────────────────────
  // Auto-snapshot on each edit. original_approved_recipe = immutable
  // baseline (first approval, before any edits). approved_recipe_history
  // = ring buffer of last 10 pre-edit snapshots. Curator can revert to
  // either via the editor modal's history panel.
  original_approved_recipe?: Record<string, unknown>;
  approved_recipe_history?: Array<{
    recipe: Record<string, unknown>;
    edited_at: string;
  }>;
  approved_recipe_updated_at?: string;
  // ─── Model relation (Jun 21) ───────────────────────────────────────
  // Third routing axis on every model-shot recipe so the synth can pick
  // wearing recipes for apparel/footwear/accessory slots and holding/using
  // recipes for skincare etc. Set by scripts/classify_model_relation.py
  // for the existing pool; new approvals will eventually get a curator
  // radio in this UI. Surfaced as a pill on every requires_model=true
  // tile so you can spot-check the classifier.
  model_relation?: 'wearing' | 'holding' | 'using';
  // ─── requires_model (read-only flag on the sidecar) ────────────────
  // Drives whether model_relation matters — pill is hidden on non-model
  // tiles since there's no model in the shot to classify.
  requires_model?: boolean;
  // ─── is_infographic (Jun 21) ───────────────────────────────────────
  // Marks this as a curated infographic recipe — clean product + callouts in
  // its OWN layout, rendered verbatim. Infographic slots pick these; standard
  // slots exclude them. Curator toggles it on the tile.
  is_infographic?: boolean;
  // ─── is_product_shot (Jun 23) ──────────────────────────────────────
  // Marks this as a curated TEXT-FREE product shot (no headline/CTA baked in;
  // may include a model). Brand-setup + top-up batches route ~25% of slots to
  // these and render them verbatim with no copy, so a batch isn't all text.
  // Curator toggles it on the tile.
  is_product_shot?: boolean;
  // ─── fusion_enabled (Jul 7) ────────────────────────────────────────
  // May this recipe take part in REALTIME_FUSION? Default true. Curator
  // sets false to exclude a recipe that doesn't port well (never a fusion
  // partner + renders verbatim as a scene). Toggled on the tile.
  fusion_enabled?: boolean;
  // ─── hide_from_templates (Jul 14) ──────────────────────────────────
  // Hide this recipe from the user-facing Templates section (matches its
  // scraped source too closely to publish). Still FULLY available to the
  // picker for fusion + scene selection. Default false (shown).
  hide_from_templates?: boolean;
  // ─── fusion_only (Jul 27) ──────────────────────────────────────────
  // "Ingredient" recipe: NEVER a standalone scene, ONLY grafted onto a real
  // recipe as a fusion partner (abstract background / typography / framing /
  // composition). fusion_only_kind labels what it contributes.
  fusion_only?: boolean;
  fusion_only_kind?: 'background' | 'typography' | 'framing' | 'composition' | null;
};

// Mirrors helpers.moodboards.PRODUCT_TYPES + routes/dev.py _PRODUCT_TYPES.
// Keep these three lists in sync.
//
// Jun 17 evening — wearable split into apparel / footwear / accessory.
// Legacy 'wearable' tag still accepted server-side (picker treats it as
// matching any of the 3 subtypes via LEGACY_TYPE_SUBSUMES) so existing
// curation continues working. UI renders the 8 modern types as the
// primary checkbox row; legacy 'wearable' shown as an amber pill ONLY
// on tiles that already carry it, so curators can re-tag at leisure.
type ProductType =
  | 'skincare' | 'haircare' | 'apparel' | 'footwear' | 'accessory'
  | 'consumable'
  // Jul 6 — consumable split into finer beverage/supplement sub-types.
  // consumable stays as a parent that subsumes these (LEGACY_TYPE_SUBSUMES),
  // so existing consumable recipes remain a fallback for the sub-types.
  | 'tea-coffee' | 'sports-drink' | 'workout-supplement'
  | 'tech' | 'home'
  // Jul 7 — distinct apparel / footwear / beauty / supplement sub-types.
  // STRICT (no subsume) — start empty until curator tags recipes.
  | 'shirts' | 'pants' | 'dresses' | 'activewear' | 'coats'
  // Jul 20 — granular footwear leaves (footwear stays the umbrella group).
  | 'sneakers' | 'boots' | 'flats' | 'heels' | 'sandals'
  // Jul 28 — color-cosmetic leaves; 'makeup' is a display group (legacy) now.
  | 'makeup' | 'face-makeup' | 'eye-makeup' | 'lip-makeup' | 'fragrance' | 'supplement'
  // Jul 13 — accessory sub-types (subsume up to accessory → wearable).
  | 'bags' | 'wallets' | 'sunglasses' | 'hats' | 'belts' | 'watches' | 'scarves'
  | 'jewelry'     // Jul 20 — own recipe-eligible leaf (was → accessory)
  | 'wearable'    // ← legacy alias subsuming apparel/footwear/accessory
  | 'universal';  // ← deprecated Jun 17 evening — existing tagged tiles
                  //   can untag; never offered as a new tag
const PRODUCT_TYPES: ProductType[] = [
  // Jul 20 — umbrella types (apparel / footwear / accessory) are DISPLAY GROUPS
  // only, moved to LEGACY below. They have ~0 recipes (the real ones live in the
  // leaves) and tagging a recipe with a bare umbrella is a near no-op, so the
  // builder offers only leaves: shirts/pants/… , sneakers/flats/… , bags/hats/…
  'skincare', 'haircare',
  'consumable', 'tea-coffee', 'sports-drink', 'workout-supplement',
  'tech', 'home',
  'shirts', 'pants', 'dresses', 'activewear', 'coats',
  'sneakers', 'boots', 'flats', 'heels', 'sandals',
  'face-makeup', 'eye-makeup', 'lip-makeup', 'fragrance', 'supplement',
  'bags', 'wallets', 'sunglasses', 'hats', 'belts', 'watches', 'scarves',
  'jewelry',
];
// Not-taggable pills, shown ONLY on tiles that already carry them so the
// curator can remove + re-tag. 'apparel' / 'footwear' / 'accessory' (Jul 20)
// are DISPLAY-GROUP labels with no recipe-matching meaning — tag the granular
// leaf instead (shirts/pants…, sneakers/flats…, bags/hats…). 'wearable' is
// legacy-but-still-matches; 'universal' is fully deprecated (picker no_match).
const LEGACY_PRODUCT_TYPES: ProductType[] = ['apparel', 'footwear', 'accessory', 'makeup', 'wearable', 'universal'];
const PRODUCT_TYPE_LABEL: Record<ProductType, string> = {
  skincare: 'Skincare',
  haircare: 'Haircare',
  apparel: 'Apparel (group)',
  footwear: 'Footwear (group)',
  accessory: 'Accessory (group)',
  consumable: 'Consumable',
  'tea-coffee': 'Tea & Coffee',
  'sports-drink': 'Sports / Energy',
  'workout-supplement': 'Workout Supp.',
  tech: 'Tech',
  home: 'Home',
  shirts: 'Shirts',
  pants: 'Pants',
  dresses: 'Dresses',
  activewear: 'Activewear',
  coats: 'Coats & Jackets',
  sneakers: 'Sneakers',
  boots: 'Boots',
  flats: 'Flats',
  heels: 'Heels',
  sandals: 'Sandals',
  makeup: 'Makeup (group)',
  'face-makeup': 'Face Makeup',
  'eye-makeup': 'Eye Makeup',
  'lip-makeup': 'Lip Makeup',
  fragrance: 'Fragrance',
  supplement: 'Vitamins & Supp.',
  bags: 'Bags',
  wallets: 'Wallets',
  sunglasses: 'Sunglasses',
  hats: 'Hats & Caps',
  belts: 'Belts',
  watches: 'Watches',
  scarves: 'Scarves',
  jewelry: 'Jewelry',
  wearable: 'Wearable (legacy)',
  universal: 'Universal (deprecated)',
};

type ListResponse = {
  vibes: Vibe[];
  groups: Record<Vibe, MoodboardRecord[]>;
  total: number;
};

function fileToDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error ?? new Error('FileReader failed'));
    reader.readAsDataURL(file);
  });
}

function dataUrlToBase64(dataUrl: string): { mime: string; b64: string } {
  // data:image/png;base64,iVBO... → { mime: 'image/png', b64: 'iVBO...' }
  const match = dataUrl.match(/^data:([^;]+);base64,(.+)$/);
  if (!match) throw new Error('Expected base64 data URL');
  return { mime: match[1], b64: match[2] };
}

export default function MoodboardBuilderPage() {
  if (!IS_DEV) {
    return (
      <div className="min-h-screen flex items-center justify-center text-dark-muted">
        Dev-only page. Set <code className="mx-2 px-2 py-1 bg-surface rounded">NODE_ENV=development</code> and reload.
      </div>
    );
  }

  // ---------- Source image ----------
  const [sourcePreview, setSourcePreview] = useState<string | null>(null);
  const [sourceFile, setSourceFile] = useState<File | null>(null);

  // ---------- Analyze step ----------
  const [analyzing, setAnalyzing] = useState(false);
  const [analyzeError, setAnalyzeError] = useState<string | null>(null);
  const [analysis, setAnalysis] = useState<AnalyzeResult | null>(null);

  // ---------- Editable fields (mirror analysis once it lands; user can override) ----------
  const [vibe, setVibe] = useState<Vibe>('Premium');
  const [tagInput, setTagInput] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [styleSummary, setStyleSummary] = useState('');
  const [paletteWords, setPaletteWords] = useState('');
  const [compositionNotes, setCompositionNotes] = useState('');

  // ---------- Save step ----------
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // ---------- Existing library ----------
  const [library, setLibrary] = useState<ListResponse | null>(null);
  const [libraryError, setLibraryError] = useState<string | null>(null);
  const [libraryLoading, setLibraryLoading] = useState(false);
  // Demo (Jul 7) — global toggle: show the baked PREVIEW render next to each
  // recipe's source image, to eyeball how closely the render matches the recipe.
  const [showPreviews, setShowPreviews] = useState(false);
  // Lightbox — click a card's split preview to see SOURCE | RENDER full-screen.
  const [lightbox, setLightbox] = useState<MoodboardRecord | null>(null);
  useEffect(() => {
    if (!lightbox) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setLightbox(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [lightbox]);

  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // ─── Drop / paste / upload handlers ────────────────────────────────
  // Jun 18 — auto-process: as soon as an image lands (paste / drop /
  // browse), analyze with AI then save to the library, no button clicks.
  // The combined flow uses the analyze RESPONSE directly (not React
  // state) for the save body, since setState wouldn't propagate in time
  // for a chained save. The manual Analyze / Save buttons stay for
  // re-runs + edits.
  const autoProcess = useCallback(async (file: File, dataUrl: string) => {
    setAnalyzing(true);
    setAnalyzeError(null);
    setSaveError(null);
    setAnalysis(null);
    let data: AnalyzeResult;
    try {
      const fd = new FormData();
      fd.append('image', file);
      const headers = await getAuthHeaders();
      delete (headers as Record<string, string>)['Content-Type'];
      const resp = await fetch(`${API_BASE}/api/dev/moodboard/analyze`, {
        method: 'POST', body: fd, headers,
      });
      if (!resp.ok) throw new Error(`${resp.status}: ${await resp.text()}`);
      data = (await resp.json()) as AnalyzeResult;
      // Reflect in the editable fields so the saved record is visible.
      setAnalysis(data);
      setVibe(data.vibe);
      setTags(data.suggested_tags ?? []);
      setStyleSummary(data.style_summary ?? '');
      setPaletteWords(data.palette_words ?? '');
      setCompositionNotes(data.composition_notes ?? '');
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : String(e);
      setAnalyzeError(`Analyze failed: ${msg} — fix + use the manual buttons.`);
      setAnalyzing(false);
      return;
    }
    setAnalyzing(false);

    // Immediately save using the analyze RESPONSE (not state).
    setSaving(true);
    try {
      const { mime, b64 } = dataUrlToBase64(dataUrl);
      const headers = await getAuthHeaders();
      headers['Content-Type'] = 'application/json';
      const resp = await fetch(`${API_BASE}/api/dev/moodboard/save`, {
        method: 'POST', headers,
        body: JSON.stringify({
          image_b64: b64,
          image_mime: mime,
          vibe: data.vibe,
          tags: data.suggested_tags ?? [],
          style_summary: data.style_summary ?? '',
          palette_words: data.palette_words ?? '',
          composition_notes: data.composition_notes ?? '',
        }),
      });
      if (!resp.ok) throw new Error(`${resp.status}: ${await resp.text()}`);
      const record = (await resp.json()) as MoodboardRecord;
      setLibrary((prev) => {
        if (!prev) {
          return {
            vibes: VIBES,
            groups: { ...Object.fromEntries(VIBES.map((v) => [v, []])), [record.vibe]: [record] } as Record<Vibe, MoodboardRecord[]>,
            total: 1,
          };
        }
        const groups = { ...prev.groups };
        groups[record.vibe] = [record, ...(groups[record.vibe] ?? [])];
        return { ...prev, groups, total: prev.total + 1 };
      });
      // Reset for the next paste.
      setSourceFile(null);
      setSourcePreview(null);
      setAnalysis(null);
      setTags([]);
      setStyleSummary('');
      setPaletteWords('');
      setCompositionNotes('');
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : String(e);
      setSaveError(`Auto-save failed: ${msg} — use the Save button to retry.`);
    } finally {
      setSaving(false);
    }
  }, []);

  const acceptFile = useCallback(async (file: File | null) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setAnalyzeError('Drop an image file (PNG/JPEG/WebP).');
      return;
    }
    const dataUrl = await fileToDataUrl(file);
    setSourcePreview(dataUrl);
    setSourceFile(file);
    setAnalysis(null);
    setAnalyzeError(null);
    setSaveError(null);
    // Auto analyze + save — zero clicks (Jun 18).
    void autoProcess(file, dataUrl);
  }, [autoProcess]);

  useEffect(() => {
    function onPaste(e: ClipboardEvent) {
      if (!e.clipboardData) return;
      for (const item of Array.from(e.clipboardData.items)) {
        if (item.type.startsWith('image/')) {
          const f = item.getAsFile();
          if (f) {
            e.preventDefault();
            void acceptFile(f);
            return;
          }
        }
      }
    }
    window.addEventListener('paste', onPaste);
    return () => window.removeEventListener('paste', onPaste);
  }, [acceptFile]);

  // ─── Analyze ───────────────────────────────────────────────────────
  const handleAnalyze = useCallback(async () => {
    if (!sourceFile) return;
    setAnalyzing(true);
    setAnalyzeError(null);
    setAnalysis(null);
    try {
      const fd = new FormData();
      fd.append('image', sourceFile);
      const headers = await getAuthHeaders();
      // Don't set Content-Type — let the browser add the multipart boundary.
      delete (headers as Record<string, string>)['Content-Type'];
      const resp = await fetch(`${API_BASE}/api/dev/moodboard/analyze`, {
        method: 'POST',
        body: fd,
        headers,
      });
      if (!resp.ok) {
        const text = await resp.text();
        throw new Error(`${resp.status}: ${text || resp.statusText}`);
      }
      const data = (await resp.json()) as AnalyzeResult;
      setAnalysis(data);
      // Seed editable fields from the vision response so the user can
      // tweak before saving.
      setVibe(data.vibe);
      setTags(data.suggested_tags ?? []);
      setStyleSummary(data.style_summary ?? '');
      setPaletteWords(data.palette_words ?? '');
      setCompositionNotes(data.composition_notes ?? '');
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : String(e);
      setAnalyzeError(`Analyze failed: ${msg}`);
    } finally {
      setAnalyzing(false);
    }
  }, [sourceFile]);

  // ─── Save ──────────────────────────────────────────────────────────
  const handleSave = useCallback(async () => {
    if (!sourcePreview) return;
    if (!styleSummary.trim() && tags.length === 0) {
      setSaveError('Add a style summary or at least one tag before saving.');
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      const { mime, b64 } = dataUrlToBase64(sourcePreview);
      const headers = await getAuthHeaders();
      headers['Content-Type'] = 'application/json';
      const resp = await fetch(`${API_BASE}/api/dev/moodboard/save`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          image_b64: b64,
          image_mime: mime,
          vibe,
          tags,
          style_summary: styleSummary,
          palette_words: paletteWords,
          composition_notes: compositionNotes,
        }),
      });
      if (!resp.ok) {
        const text = await resp.text();
        throw new Error(`${resp.status}: ${text || resp.statusText}`);
      }
      const record = (await resp.json()) as MoodboardRecord;
      // Drop the new record into the grouped library without a refetch.
      setLibrary((prev) => {
        if (!prev) {
          return {
            vibes: VIBES,
            groups: { ...Object.fromEntries(VIBES.map((v) => [v, []])), [record.vibe]: [record] } as Record<Vibe, MoodboardRecord[]>,
            total: 1,
          };
        }
        const groups = { ...prev.groups };
        groups[record.vibe] = [record, ...(groups[record.vibe] ?? [])];
        return { ...prev, groups, total: prev.total + 1 };
      });
      // Reset the form for the next image.
      setSourceFile(null);
      setSourcePreview(null);
      setAnalysis(null);
      setTags([]);
      setStyleSummary('');
      setPaletteWords('');
      setCompositionNotes('');
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : String(e);
      setSaveError(`Save failed: ${msg}`);
    } finally {
      setSaving(false);
    }
  }, [
    sourcePreview, vibe, tags, styleSummary, paletteWords, compositionNotes,
  ]);

  // ─── Library load ──────────────────────────────────────────────────
  const loadLibrary = useCallback(async () => {
    setLibraryLoading(true);
    setLibraryError(null);
    try {
      const headers = await getAuthHeaders();
      const resp = await fetch(`${API_BASE}/api/dev/moodboard/list`, {
        method: 'GET',
        headers,
      });
      if (!resp.ok) {
        const text = await resp.text();
        throw new Error(`${resp.status}: ${text || resp.statusText}`);
      }
      const data = (await resp.json()) as ListResponse;
      setLibrary(data);
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : String(e);
      setLibraryError(`List failed: ${msg}`);
    } finally {
      setLibraryLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadLibrary();
  }, [loadLibrary]);

  // ─── Delete ────────────────────────────────────────────────────────
  const handleDelete = useCallback(async (record: MoodboardRecord) => {
    if (!window.confirm(`Delete ${record.id}? This can't be undone.`)) return;
    try {
      const headers = await getAuthHeaders();
      const resp = await fetch(
        `${API_BASE}/api/dev/moodboard/${record.vibe_slug}/${record.id}`,
        { method: 'DELETE', headers },
      );
      if (!resp.ok) {
        const text = await resp.text();
        throw new Error(`${resp.status}: ${text || resp.statusText}`);
      }
      setLibrary((prev) => {
        if (!prev) return prev;
        const groups = { ...prev.groups };
        groups[record.vibe] = (groups[record.vibe] ?? []).filter((r) => r.id !== record.id);
        return { ...prev, groups, total: Math.max(0, prev.total - 1) };
      });
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : String(e);
      alert(`Delete failed: ${msg}`);
    }
  }, []);

  // ─── Tag input handlers ────────────────────────────────────────────
  const addTag = useCallback(() => {
    const trimmed = tagInput.trim();
    if (!trimmed) return;
    setTags((prev) => (prev.includes(trimmed) ? prev : [...prev, trimmed].slice(0, 12)));
    setTagInput('');
  }, [tagInput]);

  const removeTag = useCallback((t: string) => {
    setTags((prev) => prev.filter((x) => x !== t));
  }, []);

  // ─── Drop-zone handlers ────────────────────────────────────────────
  const [dragActive, setDragActive] = useState(false);

  // ─── Library counts ────────────────────────────────────────────────
  const totalCount = useMemo(() => library?.total ?? 0, [library]);

  // Approved counts: total + per-vibe map (Jun 16 — production worker
  // filters on ready_for_production=true; this surfaces the distribution
  // so curating ≥4 per vibe is at-a-glance).
  const approvedTotal = useMemo(() => {
    if (!library) return 0;
    return Object.values(library.groups).reduce(
      (sum, items) => sum + items.filter((r) => r.ready_for_production).length,
      0,
    );
  }, [library]);
  const approvedByVibe = useMemo(() => {
    const m: Record<string, number> = {};
    if (library) {
      for (const v of VIBES) {
        m[v] = (library.groups[v] ?? []).filter((r) => r.ready_for_production).length;
      }
    }
    return m;
  }, [library]);
  // Rejected counts — same shape, useful for curation visibility.
  const rejectedTotal = useMemo(() => {
    if (!library) return 0;
    return Object.values(library.groups).reduce(
      (sum, items) => sum + items.filter((r) => r.rejected).length,
      0,
    );
  }, [library]);

  // Library filter mode — replaces the old approved-only boolean.
  // 'unreviewed' shows neither-approved-nor-rejected (the curation queue).
  // 'rejected' added Jun 23 alongside the previews-style flat grid so
  // rejected tiles are reachable (used to be hidden under "all" only).
  type FilterMode = 'all' | 'approved' | 'unreviewed' | 'rejected';
  const [filterMode, setFilterMode] = useState<FilterMode>('all');

  // Product-type filter (Jun 17 evening v3) — second filter axis next
  // to the existing approval-status filter. Mirrors the smoke-test
  // page's strict matchesProductType logic so what's visible here is
  // what the production picker would consider for that product type.
  // null = no filter (show all product types).
  const [productTypeFilter, setProductTypeFilter] = useState<ProductType | 'untagged' | 'fusion-only' | null>(null);

  // Vibe filter (Jun 23) — single flat grid instead of per-vibe sections,
  // so vibe becomes a filter chip (parallels /moodboard-previews). null =
  // no vibe filter (show all vibes).
  const [vibeFilter, setVibeFilter] = useState<Vibe | null>(null);

  // Product-shot filter (Jun 23) — show only tiles tagged is_product_shot.
  // Mirrors the chip on /moodboard-previews so curators can audit the
  // tagged set from either page.
  const [productShotOnly, setProductShotOnly] = useState(false);

  // Strict match — same rules as backend _recipe_matches_product_type.
  // Untagged + universal-only no longer match when a filter is active.
  // 'wearable' legacy alias still subsumes apparel/footwear/accessory.
  // Special pseudo-filter 'untagged' surfaces moodboards with NO
  // product_types set so the curator can find them and tag them.
  const matchesProductTypeFilter = useCallback(
    (rec: MoodboardRecord, target: ProductType | 'untagged' | 'fusion-only' | null): boolean => {
      if (target === null) return true;
      // Jul 28 — fusion-only ingredients have empty product_types, so they'd
      // otherwise hide under ⚠ Untagged. Their own filter surfaces them.
      if (target === 'fusion-only') return !!rec.fusion_only;
      const types = (rec.product_types ?? []) as string[];
      // Fusion-only ingredients are DELIBERATELY product-type-free — they're not
      // "untagged/needs tagging", so keep them out of the Untagged queue (they
      // have their own 🧩 Fusion only filter above).
      if (target === 'untagged') return types.length === 0 && !rec.fusion_only;
      if (types.length === 0) return false;
      if (types.includes(target)) return true;
      // Legacy wearable subsumes apparel/footwear/accessory.
      if (types.includes('wearable') && ['apparel', 'footwear', 'accessory'].includes(target)) return true;
      return false;
    },
    [],
  );

  // ─── Move (reclassify into a different vibe) ──────────────────────
  // Per-tile popover state — only ONE tile's popover is open at a time;
  // null = none open. Use the record id since it's stable across vibes
  // (the move endpoint preserves id while only changing the path).
  const [movingId, setMovingId] = useState<string | null>(null);
  const moveRecord = useCallback(
    async (record: MoodboardRecord, targetVibe: Vibe) => {
      if (targetVibe === record.vibe) {
        setMovingId(null);
        return;
      }
      try {
        const headers = await getAuthHeaders();
        headers['Content-Type'] = 'application/json';
        const resp = await fetch(
          `${API_BASE}/api/dev/moodboard/${record.vibe_slug}/${record.id}/move`,
          {
            method: 'POST', headers,
            body: JSON.stringify({ target_vibe: targetVibe }),
          },
        );
        if (!resp.ok) {
          const text = await resp.text();
          throw new Error(`${resp.status}: ${text || resp.statusText}`);
        }
        const data = (await resp.json()) as { record: MoodboardRecord };
        // Move the record between vibe groups in our local state.
        setLibrary((prev) => {
          if (!prev) return prev;
          const groups = { ...prev.groups };
          groups[record.vibe] = (groups[record.vibe] ?? []).filter(
            (r) => r.id !== record.id,
          );
          groups[data.record.vibe] = [
            data.record,
            ...(groups[data.record.vibe] ?? []),
          ];
          return { ...prev, groups };
        });
      } catch (e: unknown) {
        const msg = e instanceof Error ? e.message : String(e);
        alert(`Move failed: ${msg}`);
      } finally {
        setMovingId(null);
      }
    },
    [],
  );

  // ─── Reject / restore ──────────────────────────────────────────────
  const setRecordStatus = useCallback(
    async (record: MoodboardRecord, status: 'rejected' | 'cleared') => {
      try {
        const headers = await getAuthHeaders();
        headers['Content-Type'] = 'application/json';
        const resp = await fetch(
          `${API_BASE}/api/dev/moodboard/${record.vibe_slug}/${record.id}/status`,
          { method: 'POST', headers, body: JSON.stringify({ status }) },
        );
        if (!resp.ok) {
          const text = await resp.text();
          throw new Error(`${resp.status}: ${text || resp.statusText}`);
        }
        const data = (await resp.json()) as { record: MoodboardRecord };
        // Patch the record in place — keep array order stable.
        setLibrary((prev) => {
          if (!prev) return prev;
          const groups = { ...prev.groups };
          groups[record.vibe] = (groups[record.vibe] ?? []).map((r) =>
            r.id === record.id ? { ...r, ...data.record } : r,
          );
          return { ...prev, groups };
        });
      } catch (e: unknown) {
        const msg = e instanceof Error ? e.message : String(e);
        alert(`Update failed: ${msg}`);
      }
    },
    [],
  );

  // ─── Recipe editor (Jun 17 evening v3) ────────────────────────────
  // Curator wants to tweak the approved spec — e.g. remove "model
  // wears glasses" from the model.styling_vibe field, or trim props
  // from product.props_around_product. Opens a modal showing every
  // string-valued field in the recipe as a labeled textarea, grouped
  // by parent key. Save POSTs the edited recipe back to the sidecar.
  const [recipeEditTarget, setRecipeEditTarget] = useState<MoodboardRecord | null>(null);
  const [recipeEditDraft, setRecipeEditDraft] = useState<Record<string, unknown> | null>(null);
  const [recipeEditSaving, setRecipeEditSaving] = useState(false);

  // Test-render state (Jun 17 evening v5) — lets the curator render ONE
  // image against the draft recipe (un-saved) so they can iterate on
  // edits without committing or running brand-setup. Calls the same
  // production render path so output mirrors what brand-setup would do.
  const [testProductFile, setTestProductFile] = useState<File | null>(null);
  const [testProductName, setTestProductName] = useState('');
  const [testBrandName, setTestBrandName] = useState('');
  const [testRendering, setTestRendering] = useState(false);
  const [testRenderUrl, setTestRenderUrl] = useState<string | null>(null);
  const [testRenderError, setTestRenderError] = useState<string | null>(null);
  const [testRenderMeta, setTestRenderMeta] = useState<{ cost: number; latency_s: number } | null>(null);

  const openRecipeEditor = useCallback((record: MoodboardRecord) => {
    const recipe = (record as unknown as { approved_recipe?: Record<string, unknown> }).approved_recipe;
    if (!recipe || typeof recipe !== 'object') {
      alert('No approved recipe on this moodboard yet — approve it via the smoke test first.');
      return;
    }
    setRecipeEditTarget(record);
    // Deep-copy so edits don't mutate the source list state.
    setRecipeEditDraft(JSON.parse(JSON.stringify(recipe)));
  }, []);

  const closeRecipeEditor = useCallback(() => {
    setRecipeEditTarget(null);
    setRecipeEditDraft(null);
    setTestProductFile(null);
    setTestProductName('');
    setTestBrandName('');
    setTestRenderUrl(null);
    setTestRenderError(null);
    setTestRenderMeta(null);
  }, []);

  const saveRecipeEdit = useCallback(async () => {
    if (!recipeEditTarget || !recipeEditDraft) return;
    setRecipeEditSaving(true);
    try {
      const headers = await getAuthHeaders();
      headers['Content-Type'] = 'application/json';
      const resp = await fetch(
        `${API_BASE}/api/dev/moodboard/${recipeEditTarget.vibe_slug}/${recipeEditTarget.id}/recipe`,
        { method: 'POST', headers, body: JSON.stringify({ recipe: recipeEditDraft }) },
      );
      if (!resp.ok) {
        const text = await resp.text();
        throw new Error(`${resp.status}: ${text || resp.statusText}`);
      }
      const data = (await resp.json()) as { record: MoodboardRecord };
      setLibrary((prev) => {
        if (!prev) return prev;
        const groups = { ...prev.groups };
        groups[recipeEditTarget.vibe] = (groups[recipeEditTarget.vibe] ?? []).map((r) =>
          r.id === recipeEditTarget.id ? { ...r, ...data.record } : r,
        );
        return { ...prev, groups };
      });
      closeRecipeEditor();
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : String(e);
      alert(`Recipe save failed: ${msg}`);
    } finally {
      setRecipeEditSaving(false);
    }
  }, [recipeEditTarget, recipeEditDraft, closeRecipeEditor]);

  // Test-render the draft recipe against a user-supplied product
  // (Jun 17 evening v5). Calls /api/dev/moodboard/recipe-test-render
  // which runs render_from_approved_recipe — same fn brand-setup uses.
  // No save, no logo, no concept copy. Just the render.
  const runTestRender = useCallback(async () => {
    if (!recipeEditDraft) {
      setTestRenderError('No draft recipe to render.');
      return;
    }
    if (!testProductFile) {
      setTestRenderError('Pick a product image first.');
      return;
    }
    setTestRendering(true);
    setTestRenderError(null);
    setTestRenderUrl(null);
    setTestRenderMeta(null);
    try {
      // File → base64 string (strip the data URL prefix)
      const dataUrl = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = () => reject(new Error('Failed to read product file.'));
        reader.readAsDataURL(testProductFile);
      });
      const b64 = dataUrl.split(',', 2)[1] ?? '';
      if (!b64) throw new Error('Empty product file.');

      const headers = await getAuthHeaders();
      headers['Content-Type'] = 'application/json';
      const resp = await fetch(`${API_BASE}/api/dev/moodboard/recipe-test-render`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          recipe: recipeEditDraft,
          product_image_b64: b64,
          product_mime: testProductFile.type || 'image/png',
          product_name: testProductName.trim() || 'the product',
          brand_name: testBrandName.trim(),
          // Defaults — matches production brand-setup ai_magic slot at 1:1.
          kind: 'ai_magic',
          aspect_ratio: '1:1',
        }),
      });
      if (!resp.ok) {
        const text = await resp.text();
        throw new Error(`${resp.status}: ${text || resp.statusText}`);
      }
      const data = (await resp.json()) as { image_b64: string; cost: number; latency_s: number };
      setTestRenderUrl(`data:image/png;base64,${data.image_b64}`);
      setTestRenderMeta({ cost: data.cost, latency_s: data.latency_s });
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : String(e);
      setTestRenderError(msg);
    } finally {
      setTestRendering(false);
    }
  }, [recipeEditDraft, testProductFile, testProductName, testBrandName]);

  // Mutates the draft in place at a dotted path, e.g. "model.styling_vibe".
  const setDraftAtPath = useCallback((path: string, value: string) => {
    setRecipeEditDraft((prev) => {
      if (!prev) return prev;
      const next = JSON.parse(JSON.stringify(prev)) as Record<string, unknown>;
      const parts = path.split('.');
      let cursor: Record<string, unknown> = next;
      for (let i = 0; i < parts.length - 1; i++) {
        const k = parts[i];
        if (typeof cursor[k] !== 'object' || cursor[k] === null) cursor[k] = {};
        cursor = cursor[k] as Record<string, unknown>;
      }
      cursor[parts[parts.length - 1]] = value;
      return next;
    });
  }, []);

  // ─── Product type tagging (Jun 17) ─────────────────────────────────
  // Toggle a single product_type on/off for a moodboard. POSTs the
  // resulting list to /product-types and patches the record in place.
  const toggleProductType = useCallback(
    async (record: MoodboardRecord, type: ProductType) => {
      const current = record.product_types ?? [];
      const next = current.includes(type)
        ? current.filter((t) => t !== type)
        : [...current, type];
      try {
        const headers = await getAuthHeaders();
        headers['Content-Type'] = 'application/json';
        const resp = await fetch(
          `${API_BASE}/api/dev/moodboard/${record.vibe_slug}/${record.id}/product-types`,
          { method: 'POST', headers, body: JSON.stringify({ product_types: next }) },
        );
        if (!resp.ok) {
          const text = await resp.text();
          throw new Error(`${resp.status}: ${text || resp.statusText}`);
        }
        const data = (await resp.json()) as { product_types: ProductType[] };
        setLibrary((prev) => {
          if (!prev) return prev;
          const groups = { ...prev.groups };
          groups[record.vibe] = (groups[record.vibe] ?? []).map((r) =>
            r.id === record.id ? { ...r, product_types: data.product_types } : r,
          );
          return { ...prev, groups };
        });
      } catch (e: unknown) {
        const msg = e instanceof Error ? e.message : String(e);
        alert(`Product type update failed: ${msg}`);
      }
    },
    [],
  );

  // ─── Render-routing curator controls (Jun 21) ──────────────────────
  // Set model_relation (wearing/holding/using) and/or is_infographic on a
  // recipe. Only the passed keys are sent. POSTs to /routing + patches in place.
  const setRouting = useCallback(
    async (
      record: MoodboardRecord,
      patch: {
        model_relation?: 'wearing' | 'holding' | 'using' | null;
        is_infographic?: boolean;
        is_product_shot?: boolean;
        requires_model?: boolean;
        fusion_enabled?: boolean;
        hide_from_templates?: boolean;
        fusion_only?: boolean;
        fusion_only_kind?: 'background' | 'typography' | 'framing' | 'composition' | null;
      },
    ) => {
      try {
        const headers = await getAuthHeaders();
        headers['Content-Type'] = 'application/json';
        const resp = await fetch(
          `${API_BASE}/api/dev/moodboard/${record.vibe_slug}/${record.id}/routing`,
          { method: 'POST', headers, body: JSON.stringify(patch) },
        );
        if (!resp.ok) {
          const text = await resp.text();
          throw new Error(`${resp.status}: ${text || resp.statusText}`);
        }
        const data = (await resp.json()) as {
          requires_model: boolean;
          model_relation: 'wearing' | 'holding' | 'using' | null;
          is_infographic: boolean;
          is_product_shot: boolean;
          fusion_enabled: boolean;
          hide_from_templates: boolean;
          fusion_only: boolean;
          fusion_only_kind: 'background' | 'typography' | 'framing' | 'composition' | null;
        };
        setLibrary((prev) => {
          if (!prev) return prev;
          const groups = { ...prev.groups };
          groups[record.vibe] = (groups[record.vibe] ?? []).map((r) =>
            r.id === record.id
              ? {
                  ...r,
                  requires_model: data.requires_model,
                  model_relation: data.model_relation ?? undefined,
                  is_infographic: data.is_infographic,
                  is_product_shot: data.is_product_shot,
                  fusion_enabled: data.fusion_enabled,
                  hide_from_templates: data.hide_from_templates,
                  fusion_only: data.fusion_only,
                  fusion_only_kind: data.fusion_only_kind ?? undefined,
                }
              : r,
          );
          return { ...prev, groups };
        });
      } catch (e: unknown) {
        const msg = e instanceof Error ? e.message : String(e);
        alert(`Routing update failed: ${msg}`);
      }
    },
    [],
  );

  return (
    <div className="min-h-screen bg-surface p-6">
      {/* Full-width — was max-w-[1400px] mx-auto, dropped Jun 23 so the
          library grid uses every available pixel on wide displays. */}
      <div className="w-full">
        {/* Header */}
        <header className="mb-6">
          <div className="flex items-baseline gap-3 flex-wrap">
            <h1 className="text-2xl font-bold text-dark">Moodboard Builder</h1>
            <span className="text-sm text-dark-muted">dev · {totalCount} saved</span>
            {approvedTotal > 0 && (
              <span
                className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700 text-xs font-semibold border border-emerald-200"
                title="Approved moodboards are routed through the text-only production render path (gpt-image-2 + harmonized palette, no moodboard image transferred)."
              >
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                {approvedTotal} approved
              </span>
            )}
            {rejectedTotal > 0 && (
              <span
                className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-red-50 text-red-700 text-xs font-semibold border border-red-200"
                title="Rejected moodboards are explicitly NOT used by production (rejection also strips approval flags)."
              >
                <span className="w-1.5 h-1.5 rounded-full bg-red-500" />
                {rejectedTotal} rejected
              </span>
            )}
          </div>
          <div className="flex items-center justify-between gap-4 mt-1 flex-wrap">
            <p className="text-sm text-dark-muted">
              Drop, paste (⌘V), or upload an inspiration image. Vision classifies it; you tweak; save anchors it to a vibe folder in GCS for the ad-render pipeline.
            </p>
            <a
              href="/moodboard-previews"
              className="px-3 py-1.5 rounded-full text-xs font-semibold bg-primary text-white hover:bg-primary-hover whitespace-nowrap"
              title="Bake preview thumbnails so the production picker can show recipes (not scraped moodboards)."
            >
              Generate previews →
            </a>
          </div>
        </header>

        {/* Composer */}
        <section className="grid grid-cols-1 lg:grid-cols-[1fr_1fr] gap-6 mb-10">
          {/* Drop zone / preview */}
          <div
            onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
            onDragLeave={() => setDragActive(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDragActive(false);
              const f = e.dataTransfer.files?.[0];
              if (f) void acceptFile(f);
            }}
            className={`relative aspect-square rounded-2xl border-2 border-dashed transition-colors overflow-hidden flex items-center justify-center text-center bg-white ${
              dragActive ? 'border-primary bg-primary-light/30' : 'border-surface-dark/50'
            }`}
          >
            {sourcePreview ? (
              <>
                <img src={sourcePreview} className="absolute inset-0 w-full h-full object-contain bg-surface" alt="source" />
                <button
                  type="button"
                  onClick={() => { setSourceFile(null); setSourcePreview(null); setAnalysis(null); }}
                  className="absolute top-3 right-3 z-10 w-8 h-8 rounded-full bg-white shadow text-dark hover:bg-red-50 hover:text-red-600 flex items-center justify-center"
                  aria-label="Clear image"
                >
                  ×
                </button>
              </>
            ) : (
              <div className="px-6">
                <div className="text-dark-muted text-sm mb-3">
                  Drop image · paste from clipboard · or
                </div>
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="px-4 py-2 rounded-full bg-primary text-white font-medium text-sm"
                >
                  Browse files
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) void acceptFile(f);
                    e.target.value = '';
                  }}
                />
                <div className="text-xs text-dark-muted/70 mt-4">
                  PNG · JPG · WebP &nbsp;·&nbsp; backend resizes to 1024² WebP
                </div>
              </div>
            )}
          </div>

          {/* Form */}
          <div className="bg-white rounded-2xl border border-surface-dark/40 p-6 flex flex-col">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-semibold text-dark">Classify & save</h2>
              <button
                type="button"
                onClick={() => void handleAnalyze()}
                disabled={!sourceFile || analyzing}
                className="px-3 py-1.5 rounded-full bg-accent text-white text-xs font-medium disabled:opacity-40"
              >
                {analyzing ? 'Analyzing…' : analysis ? 'Re-analyze' : 'Analyze with AI'}
              </button>
            </div>

            {analyzeError && (
              <div className="mb-3 px-3 py-2 rounded-lg bg-red-50 text-red-700 text-xs">{analyzeError}</div>
            )}

            {/* Vibe */}
            <div className="mb-4">
              <label className="block text-xs font-semibold uppercase tracking-wide text-dark-muted mb-2">
                Vibe {analysis && (
                  <span className="ml-2 normal-case tracking-normal font-normal text-dark-muted/60">
                    (suggested: {analysis.vibe} · {(analysis.confidence * 100).toFixed(0)}%)
                  </span>
                )}
              </label>
              <div className="grid grid-cols-5 gap-2">
                {VIBES.map((v) => {
                  const active = v === vibe;
                  return (
                    <button
                      key={v}
                      type="button"
                      onClick={() => setVibe(v)}
                      className={`px-2 py-2 rounded-lg text-xs font-medium border transition-colors ${
                        active
                          ? 'border-primary bg-primary-light text-primary'
                          : 'border-surface-dark/50 bg-white text-dark hover:border-dark-muted/60'
                      }`}
                    >
                      {v}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Style summary */}
            <div className="mb-4">
              <label className="block text-xs font-semibold uppercase tracking-wide text-dark-muted mb-1">
                Style summary
              </label>
              <textarea
                value={styleSummary}
                onChange={(e) => setStyleSummary(e.target.value)}
                placeholder="Aesop-style editorial — muted, restrained, magazine-like"
                rows={2}
                className="w-full px-3 py-2 rounded-lg border border-surface-dark/50 text-sm resize-none"
              />
            </div>

            {/* Palette */}
            <div className="mb-4">
              <label className="block text-xs font-semibold uppercase tracking-wide text-dark-muted mb-1">
                Palette words
              </label>
              <input
                value={paletteWords}
                onChange={(e) => setPaletteWords(e.target.value)}
                placeholder="warm beige, soft cream, sage accent"
                className="w-full px-3 py-2 rounded-lg border border-surface-dark/50 text-sm"
              />
            </div>

            {/* Composition */}
            <div className="mb-4">
              <label className="block text-xs font-semibold uppercase tracking-wide text-dark-muted mb-1">
                Composition notes
              </label>
              <input
                value={compositionNotes}
                onChange={(e) => setCompositionNotes(e.target.value)}
                placeholder="asymmetric, generous negative space, off-center hero"
                className="w-full px-3 py-2 rounded-lg border border-surface-dark/50 text-sm"
              />
            </div>

            {/* Tags */}
            <div className="mb-4">
              <label className="block text-xs font-semibold uppercase tracking-wide text-dark-muted mb-1">
                Tags
              </label>
              <div className="flex flex-wrap gap-2 mb-2">
                {tags.map((t) => (
                  <span key={t} className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-primary-light text-primary text-xs">
                    {t}
                    <button onClick={() => removeTag(t)} className="ml-1 hover:text-red-600" aria-label={`Remove ${t}`}>×</button>
                  </span>
                ))}
                {tags.length === 0 && <span className="text-xs text-dark-muted/70">No tags yet</span>}
              </div>
              <div className="flex gap-2">
                <input
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') { e.preventDefault(); addTag(); }
                  }}
                  placeholder="Add a tag, then Enter"
                  className="flex-1 px-3 py-2 rounded-lg border border-surface-dark/50 text-sm"
                />
                <button
                  type="button"
                  onClick={addTag}
                  className="px-3 py-2 rounded-lg bg-surface-dark/40 text-dark text-sm"
                >
                  Add
                </button>
              </div>
            </div>

            {saveError && (
              <div className="mb-3 px-3 py-2 rounded-lg bg-red-50 text-red-700 text-xs">{saveError}</div>
            )}

            <div className="mt-auto flex justify-end">
              <button
                type="button"
                onClick={() => void handleSave()}
                disabled={!sourcePreview || saving}
                className="px-5 py-2.5 rounded-full bg-primary text-white font-medium text-sm disabled:opacity-40"
              >
                {saving ? 'Saving…' : 'Save to moodboard'}
              </button>
            </div>
          </div>
        </section>

        {/* Library */}
        <section>
          {/* Library — flat grid + previews-style filter bar (Jun 23).
              Single horizontal filter row replaces the previous header +
              per-vibe sections. Filters compose: vibe + product type +
              status + product-shot tag. Vibe-sparse warning is now a chip
              inside the count strip below (only when ONE vibe is filtered). */}
          <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
            <h2 className="text-lg font-semibold text-dark">Library</h2>
            <button
              type="button"
              onClick={() => void loadLibrary()}
              disabled={libraryLoading}
              className="text-xs text-dark-muted hover:text-dark"
            >
              {libraryLoading ? 'Refreshing…' : '↻ Refresh'}
            </button>
          </div>

          {/* Filter bar — single horizontal row, wraps on narrow viewports.
              Mirrors /moodboard-previews. Dividers separate logical groups:
              vibe · product type · status · product-shot. */}
          <div className="mb-4 flex flex-wrap items-center gap-1.5 px-3 py-2 rounded-lg bg-surface/60 border border-surface-dark/30">
            {/* Vibe — All + 5 vibes */}
            <FilterChip
              label="All vibes"
              active={vibeFilter === null}
              onClick={() => setVibeFilter(null)}
            />
            {VIBES.map((v) => (
              <FilterChip
                key={v}
                label={v}
                active={vibeFilter === v}
                onClick={() => setVibeFilter(v)}
              />
            ))}
            <span className="w-px h-5 bg-surface-dark/40 mx-1" />

            {/* Product type — All + Fusion-only + types + Untagged */}
            <FilterChip
              label="All types"
              active={productTypeFilter === null}
              onClick={() => setProductTypeFilter(null)}
            />
            <FilterChip
              label="🧩 Fusion only"
              active={productTypeFilter === 'fusion-only'}
              onClick={() => setProductTypeFilter('fusion-only')}
              tone="violet"
            />
            {PRODUCT_TYPES.map((t) => (
              <FilterChip
                key={t}
                label={PRODUCT_TYPE_LABEL[t]}
                active={productTypeFilter === t}
                onClick={() => setProductTypeFilter(t)}
              />
            ))}
            <FilterChip
              label="⚠ Untagged"
              active={productTypeFilter === 'untagged'}
              onClick={() => setProductTypeFilter('untagged')}
              tone="amber"
            />
            <span className="w-px h-5 bg-surface-dark/40 mx-1" />

            {/* Status — All / Approved / Unreviewed / Rejected */}
            {([
              { id: 'all', label: 'All status' },
              { id: 'approved', label: '✓ Approved' },
              { id: 'unreviewed', label: 'Unreviewed' },
              { id: 'rejected', label: '✕ Rejected' },
            ] as { id: FilterMode; label: string }[]).map((opt) => (
              <FilterChip
                key={opt.id}
                label={opt.label}
                active={filterMode === opt.id}
                onClick={() => setFilterMode(opt.id)}
              />
            ))}
            <span className="w-px h-5 bg-surface-dark/40 mx-1" />

            {/* Product-shot — single toggle */}
            <FilterChip
              label={`📦 Product shot${productShotOnly ? ' ✓' : ''}`}
              active={productShotOnly}
              onClick={() => setProductShotOnly((v) => !v)}
            />
            <span className="w-px h-5 bg-surface-dark/40 mx-1" />
            {/* Demo — global preview toggle: show the baked render next to
                each recipe's source image to compare match. */}
            <button
              type="button"
              onClick={() => setShowPreviews((v) => !v)}
              className={`px-3 py-1 rounded-full text-xs font-bold transition ${showPreviews
                ? 'bg-primary text-white shadow-sm'
                : 'bg-surface text-dark-muted border border-surface-dark/40 hover:border-primary/50'}`}
            >
              {showPreviews ? '🖼 Previews ON' : '🖼 Show previews'}
            </button>
          </div>

          {libraryError && (
            <div className="mb-3 px-3 py-2 rounded-lg bg-red-50 text-red-700 text-xs">{libraryError}</div>
          )}

          {!library && !libraryError && (
            <div className="text-sm text-dark-muted">Loading…</div>
          )}

          {library && (() => {
            // Flatten + apply every filter (Jun 23 — was per-vibe map).
            // Vibe filter narrows the source groups; everything else is a
            // simple chain of predicates so the order is human-readable.
            const sourceVibes = vibeFilter === null ? VIBES : [vibeFilter];
            const allFiltered: MoodboardRecord[] = [];
            for (const v of sourceVibes) {
              for (const rec of library.groups[v] ?? []) {
                if (productTypeFilter !== null
                    && !matchesProductTypeFilter(rec, productTypeFilter)) continue;
                if (filterMode === 'approved' && !rec.ready_for_production) continue;
                if (filterMode === 'unreviewed'
                    && (rec.ready_for_production || rec.rejected)) continue;
                if (filterMode === 'rejected' && !rec.rejected) continue;
                if (productShotOnly && !rec.is_product_shot) continue;
                allFiltered.push(rec);
              }
            }

            // When a single vibe is filtered, surface the sparse-pool
            // warning inline (the per-vibe sections used to show this
            // automatically; now it appears only when relevant). ≥4
            // approved recipes per vibe = zero repeats in a brand-setup
            // batch (max 4 slots/vibe).
            let vibeApprovalNote: React.ReactNode = null;
            if (vibeFilter !== null) {
              const approvedCount = approvedByVibe[vibeFilter] ?? 0;
              const sparse = approvedCount > 0 && approvedCount < 4;
              if (approvedCount > 0) {
                vibeApprovalNote = (
                  <span
                    className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                      sparse
                        ? 'bg-amber-50 text-amber-700 border border-amber-200'
                        : 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                    }`}
                    title={
                      sparse
                        ? `${approvedCount} approved — under 4 means slots of this vibe may repeat in a batch. Approve more to diversify.`
                        : `${approvedCount} approved — production worker has variety.`
                    }
                  >
                    <span className={`w-1.5 h-1.5 rounded-full ${
                      sparse ? 'bg-amber-500' : 'bg-emerald-500'
                    }`} />
                    {approvedCount} approved in {vibeFilter}
                    {sparse && <span className="ml-0.5 text-amber-600/80">(aim ≥4)</span>}
                  </span>
                );
              }
            }

            return (
              <>
                <div className="flex items-baseline gap-3 mb-3 flex-wrap">
                  <span className="text-sm font-semibold text-dark">
                    {allFiltered.length} result{allFiltered.length === 1 ? '' : 's'}
                  </span>
                  {vibeApprovalNote}
                </div>
                {allFiltered.length === 0 ? (
                  <div className="text-sm text-dark-muted/70 italic py-8 text-center">
                    No moodboards match the current filters.
                  </div>
                ) : (
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3">
                    {allFiltered.map((rec) => {
                      const approved = !!rec.ready_for_production;
                      const rejected = !!rec.rejected;
                      const ringClass = approved
                        ? 'border-emerald-300/80 ring-1 ring-emerald-200'
                        : rejected
                          ? 'border-red-300/80 ring-1 ring-red-200'
                          : 'border-surface-dark/40';
                      // Tile redesigned to match /moodboard-previews (Jun 23):
                      // image area on top, caption strip BELOW with vibe + types
                      // + small status icons. Heavy curator controls (per-type
                      // toggle, model_relation, infographic/product_shot
                      // toggles) live in the recipe editor modal now — not on
                      // every tile. Hover still reveals quick actions
                      // (Reclassify / Edit / Reject / Delete).
                      const fullId = `${rec.vibe_slug}/${rec.id}`;
                      const shortId = rec.id.length > 12
                        ? `…${rec.id.slice(-10)}`
                        : rec.id;
                      const modelRel = rec.model_relation;
                      const modelRelCls = modelRel === 'wearing'
                        ? 'bg-emerald-500 text-white'
                        : modelRel === 'holding'
                          ? 'bg-blue-500 text-white'
                          : modelRel === 'using'
                            ? 'bg-violet-500 text-white'
                            : 'bg-amber-500 text-white';
                      return (
                        <div
                          key={rec.id}
                          className={`group relative rounded-xl overflow-hidden border bg-white flex flex-col ${ringClass}`}
                        >
                          {/* IMAGE — aspect-square, top of the tile */}
                          <div className="relative aspect-square overflow-hidden">
                            {showPreviews && rec.preview_url ? (
                              // Demo split — SOURCE moodboard | RENDER preview,
                              // side-by-side to eyeball how well the render matches.
                              <div
                                className="absolute inset-0 flex cursor-zoom-in"
                                onClick={(e) => { e.stopPropagation(); setLightbox(rec); }}
                                title="Click to view full-screen"
                              >
                                <div className="relative w-1/2 h-full border-r border-surface-dark/40 bg-surface">
                                  <img src={rec.image_url} alt="source" loading="lazy"
                                    className={`w-full h-full object-contain ${rejected ? 'opacity-50 grayscale' : ''}`} />
                                  <span className="absolute bottom-1 left-1 px-1 py-0.5 rounded text-[8px] font-bold bg-black/70 text-white">SOURCE</span>
                                </div>
                                <div className="relative w-1/2 h-full bg-surface">
                                  <img src={rec.preview_url} alt="render" loading="lazy"
                                    className="w-full h-full object-contain" />
                                  <span className="absolute bottom-1 left-1 px-1 py-0.5 rounded text-[8px] font-bold bg-primary text-white">RENDER</span>
                                </div>
                              </div>
                            ) : (
                              <img
                                src={rec.image_url}
                                alt={rec.style_summary || rec.id}
                                className={`w-full h-full object-cover ${rejected ? 'opacity-50 grayscale' : ''}`}
                                loading="lazy"
                              />
                            )}
                            {/* Status badge top-left — only when explicit */}
                            {approved && (
                              <span className="absolute top-1.5 left-1.5 px-1.5 py-0.5 rounded text-[9px] font-bold bg-emerald-500 text-white shadow-sm">
                                ✓ APPROVED
                              </span>
                            )}
                            {rejected && (
                              <span className="absolute top-1.5 left-1.5 px-1.5 py-0.5 rounded text-[9px] font-bold bg-red-500 text-white shadow-sm">
                                ✕ REJECTED
                              </span>
                            )}
                            {/* Short ID chip — top-right, copy-to-clipboard */}
                            <div
                              role="button"
                              tabIndex={-1}
                              onClick={(e) => {
                                e.stopPropagation();
                                e.preventDefault();
                                void navigator.clipboard?.writeText(fullId);
                              }}
                              title={`Click to copy ${fullId}`}
                              className="absolute top-1.5 right-1.5 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-black/70 backdrop-blur-sm text-white text-[10px] font-mono hover:bg-black/90 cursor-pointer"
                            >
                              <i className="ri-file-copy-line text-[10px]" />
                              <span>{shortId}</span>
                            </div>
                            {/* Hover-revealed action cluster — bottom of image */}
                            <div
                              className={`absolute inset-x-0 bottom-0 flex items-center justify-end gap-1.5 px-2 py-2 bg-gradient-to-t from-black/70 to-transparent transition-opacity ${
                                movingId === rec.id
                                  ? 'opacity-100'
                                  : 'opacity-0 group-hover:opacity-100'
                              }`}
                            >
                              <button
                                type="button"
                                onClick={() =>
                                  setMovingId(movingId === rec.id ? null : rec.id)
                                }
                                className={`w-8 h-8 rounded-full text-sm flex items-center justify-center transition-colors ${
                                  movingId === rec.id
                                    ? 'bg-primary text-white'
                                    : 'bg-white/95 text-dark hover:bg-primary hover:text-white'
                                }`}
                                title="Reclassify — move to a different vibe"
                                aria-label={`Reclassify ${rec.id}`}
                              >
                                <span aria-hidden>↻</span>
                              </button>
                              {rec.ready_for_production && (
                                <button
                                  type="button"
                                  onClick={() => openRecipeEditor(rec)}
                                  className="w-8 h-8 rounded-full bg-white/95 text-dark text-sm flex items-center justify-center hover:bg-primary hover:text-white transition-colors"
                                  title="Edit recipe — open the full editor"
                                  aria-label={`Edit recipe ${rec.id}`}
                                >
                                  <span aria-hidden>✎</span>
                                </button>
                              )}
                              {rejected ? (
                                <button
                                  type="button"
                                  onClick={() => void setRecordStatus(rec, 'cleared')}
                                  className="px-2 h-8 rounded-full bg-white/95 text-dark text-[11px] font-semibold flex items-center justify-center hover:bg-emerald-500 hover:text-white transition-colors"
                                  title="Restore — clear rejected status"
                                >
                                  Restore
                                </button>
                              ) : (
                                <button
                                  type="button"
                                  onClick={() => void setRecordStatus(rec, 'rejected')}
                                  className="w-8 h-8 rounded-full bg-white/95 text-dark text-sm flex items-center justify-center hover:bg-red-500 hover:text-white transition-colors"
                                  title="Reject — mark not-for-production"
                                  aria-label={`Reject ${rec.id}`}
                                >
                                  <span aria-hidden>✕</span>
                                </button>
                              )}
                              <button
                                type="button"
                                onClick={() => void handleDelete(rec)}
                                className="w-8 h-8 rounded-full bg-white/95 text-dark text-sm flex items-center justify-center hover:bg-red-500 hover:text-white transition-colors"
                                title="Delete — removes image + sidecar from GCS"
                                aria-label={`Delete ${rec.id}`}
                              >
                                <span aria-hidden>🗑</span>
                              </button>
                            </div>
                            {/* Reclassify popover */}
                            {movingId === rec.id && (
                              <div
                                className="absolute inset-x-2 top-9 z-10 bg-white rounded-xl shadow-lg border border-surface-dark/40 p-2"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <div className="text-[10px] font-semibold uppercase tracking-wide text-dark-muted mb-1.5 px-1">
                                  Move to vibe
                                </div>
                                <div className="flex flex-col gap-0.5">
                                  {VIBES.map((v) => {
                                    const isCurrent = v === rec.vibe;
                                    return (
                                      <button
                                        key={v}
                                        type="button"
                                        disabled={isCurrent}
                                        onClick={() => void moveRecord(rec, v)}
                                        className={`w-full text-left px-2 py-1 rounded-md text-xs transition-colors ${
                                          isCurrent
                                            ? 'bg-primary-light text-primary font-semibold cursor-default'
                                            : 'hover:bg-surface text-dark'
                                        }`}
                                      >
                                        {v}
                                        {isCurrent && (
                                          <span className="ml-1.5 text-[10px] text-primary/70">
                                            (current)
                                          </span>
                                        )}
                                      </button>
                                    );
                                  })}
                                </div>
                                <button
                                  type="button"
                                  onClick={() => setMovingId(null)}
                                  className="w-full mt-1 px-2 py-1 rounded-md text-[10px] uppercase tracking-wide text-dark-muted hover:text-dark"
                                >
                                  Cancel
                                </button>
                              </div>
                            )}
                          </div>

                          {/* CAPTION — below the image. Vibe + types + routing
                              text buttons. Jun 23 update: dropped emoji icons
                              for text labels (easier to read at a glance), and
                              bumped every pill font from text-[9px]/text-[10px]
                              to text-xs (12px) so labels are actually legible. */}
                          <div className="px-2.5 py-2.5 space-y-2 border-t border-surface-dark/30">
                            <div className="flex items-center justify-between gap-2 min-h-[20px]">
                              <span className="text-sm font-semibold text-dark truncate">{rec.vibe}</span>
                            </div>
                            {/* Product-types row — clickable pills. Click any
                                to toggle. Set = primary-filled, unset = ghost.
                                Legacy types render only when set (curator can
                                remove them). */}
                            <div className="flex flex-wrap gap-1 min-h-[22px]">
                              {PRODUCT_TYPES.map((t) => {
                                const on = (rec.product_types ?? []).includes(t);
                                return (
                                  <button
                                    key={t}
                                    type="button"
                                    onClick={() => void toggleProductType(rec, t)}
                                    className={`px-2 py-0.5 rounded text-xs font-semibold uppercase tracking-wide transition-colors ${
                                      on
                                        ? 'bg-primary text-white'
                                        : 'bg-surface text-dark-muted/70 hover:bg-surface-dark/40 hover:text-dark'
                                    }`}
                                    title={`${on ? 'Remove' : 'Add'} ${PRODUCT_TYPE_LABEL[t]} tag`}
                                  >
                                    {PRODUCT_TYPE_LABEL[t]}
                                  </button>
                                );
                              })}
                              {LEGACY_PRODUCT_TYPES.map((t) => {
                                const on = (rec.product_types ?? []).includes(t);
                                if (!on) return null;
                                return (
                                  <button
                                    key={t}
                                    type="button"
                                    onClick={() => void toggleProductType(rec, t)}
                                    className="px-2 py-0.5 rounded bg-amber-100 text-amber-900 border border-amber-300 text-xs font-semibold uppercase tracking-wide hover:bg-amber-200 transition-colors"
                                    title={`Remove legacy ${PRODUCT_TYPE_LABEL[t]} tag — superseded by apparel / footwear / accessory`}
                                  >
                                    {PRODUCT_TYPE_LABEL[t]} ✕
                                  </button>
                                );
                              })}
                            </div>
                            {/* Routing row — text buttons, not icons. Product
                                shot + Infographic are click-to-toggle on every
                                approved tile. Model relation is a 3-way set
                                of buttons (wearing/holding/using) shown only on
                                model-shot tiles. Same setRouting backend call
                                as the modal, so the two stay in sync. Jul 1:
                                show on UNREVIEWED + approved (was approved-only)
                                so recipes can be tagged during onboarding,
                                BEFORE render+approve. Hidden on rejected. */}
                            {!rejected && (
                              <div className="flex flex-wrap items-center gap-1 pt-1 border-t border-surface-dark/20">
                                {/* Compact ICON toggles (Jul 7) — Product shot /
                                    Fusable / Infographic, to free row space for
                                    the category tags. Tooltip carries meaning. */}
                                <button
                                  type="button"
                                  onClick={() =>
                                    void setRouting(rec, { is_product_shot: !rec.is_product_shot })
                                  }
                                  className={`w-7 h-7 rounded flex items-center justify-center text-sm transition-colors ${
                                    rec.is_product_shot
                                      ? 'bg-emerald-500 text-white hover:bg-emerald-600'
                                      : 'bg-surface text-dark-muted/70 hover:bg-emerald-100 hover:text-emerald-800'
                                  }`}
                                  title={
                                    rec.is_product_shot
                                      ? 'Product shot (tagged) — click to remove'
                                      : 'Tag as text-free product shot (no copy baked in)'
                                  }
                                >
                                  📦
                                </button>
                                <button
                                  type="button"
                                  onClick={() =>
                                    void setRouting(rec, { fusion_enabled: rec.fusion_enabled === false })
                                  }
                                  className={`w-7 h-7 rounded flex items-center justify-center text-sm transition-colors ${
                                    rec.fusion_enabled === false
                                      ? 'bg-red-500 text-white hover:bg-red-600'
                                      : 'bg-surface text-dark-muted/70 hover:bg-indigo-100 hover:text-indigo-800'
                                  }`}
                                  title={
                                    rec.fusion_enabled === false
                                      ? 'Excluded from realtime fusion — click to allow'
                                      : 'Fusable in realtime fusion — click to exclude (renders verbatim, never a fusion partner)'
                                  }
                                >
                                  {rec.fusion_enabled === false ? '⊘' : '🔀'}
                                </button>
                                <button
                                  type="button"
                                  onClick={() =>
                                    void setRouting(rec, {
                                      fusion_only: !rec.fusion_only,
                                      ...(!rec.fusion_only && !rec.fusion_only_kind
                                        ? { fusion_only_kind: 'background' as const }
                                        : {}),
                                    })
                                  }
                                  className={`w-7 h-7 rounded flex items-center justify-center text-sm transition-colors ${
                                    rec.fusion_only
                                      ? 'bg-purple-500 text-white hover:bg-purple-600'
                                      : 'bg-surface text-dark-muted/70 hover:bg-indigo-100 hover:text-indigo-800'
                                  }`}
                                  title={
                                    rec.fusion_only
                                      ? 'Fusion-only ingredient — never a standalone scene, only a fusion partner. Click to unset.'
                                      : 'Mark as fusion-only ingredient (never standalone; only grafted onto a recipe as a fusion partner)'
                                  }
                                >
                                  🧩
                                </button>
                                {rec.fusion_only ? (
                                  <select
                                    value={rec.fusion_only_kind ?? 'background'}
                                    onChange={(e) =>
                                      void setRouting(rec, {
                                        fusion_only_kind: e.target.value as
                                          | 'background'
                                          | 'typography'
                                          | 'framing'
                                          | 'composition',
                                      })
                                    }
                                    className="h-7 rounded bg-surface text-dark-muted/80 text-xs px-1 border border-surface-dark/40"
                                    title="What this fusion ingredient contributes"
                                  >
                                    <option value="background">background</option>
                                    <option value="typography">typography</option>
                                    <option value="framing">framing</option>
                                    <option value="composition">composition</option>
                                  </select>
                                ) : null}
                                <button
                                  type="button"
                                  onClick={() =>
                                    void setRouting(rec, { hide_from_templates: !rec.hide_from_templates })
                                  }
                                  className={`w-7 h-7 rounded flex items-center justify-center text-sm transition-colors ${
                                    rec.hide_from_templates
                                      ? 'bg-red-500 text-white hover:bg-red-600'
                                      : 'bg-surface text-dark-muted/70 hover:bg-indigo-100 hover:text-indigo-800'
                                  }`}
                                  title={
                                    rec.hide_from_templates
                                      ? 'Hidden from Templates — click to show to users (still fusable)'
                                      : 'Shown in Templates — click to hide from users (stays available for fusion)'
                                  }
                                >
                                  {rec.hide_from_templates ? '🚫' : '🖼'}
                                </button>
                                <button
                                  type="button"
                                  onClick={() =>
                                    void setRouting(rec, { is_infographic: !rec.is_infographic })
                                  }
                                  className={`w-7 h-7 rounded flex items-center justify-center text-sm transition-colors ${
                                    rec.is_infographic
                                      ? 'bg-fuchsia-500 text-white hover:bg-fuchsia-600'
                                      : 'bg-surface text-dark-muted/70 hover:bg-fuchsia-100 hover:text-fuchsia-800'
                                  }`}
                                  title={
                                    rec.is_infographic
                                      ? 'Infographic (tagged) — click to remove'
                                      : 'Tag as infographic recipe'
                                  }
                                >
                                  📊
                                </button>
                                {/* Model toggle — turns a recipe INTO a model
                                    shot (worn / held / used). Off clears the
                                    relation. When on, the 3-way relation row
                                    below appears. */}
                                <button
                                  type="button"
                                  onClick={() =>
                                    void setRouting(rec, { requires_model: !rec.requires_model })
                                  }
                                  className={`px-2 py-0.5 rounded text-xs font-semibold uppercase tracking-wide transition-colors ${
                                    rec.requires_model
                                      ? 'bg-amber-500 text-white hover:bg-amber-600'
                                      : 'bg-surface text-dark-muted/70 hover:bg-amber-100 hover:text-amber-800'
                                  }`}
                                  title={
                                    rec.requires_model
                                      ? 'Renders with a human model — click to make it product-only'
                                      : 'Tag as a model shot (worn / held / used)'
                                  }
                                >
                                  {rec.requires_model ? '✓ Model' : 'Model'}
                                </button>
                                {/* Model-relation 3-way — only on model-shot
                                    tiles. Wearing=emerald · Holding=blue ·
                                    Using=violet. Same setRouting call. */}
                                {rec.requires_model && (
                                  <>
                                    <span className="text-[10px] uppercase tracking-wide text-dark-muted ml-1">Model:</span>
                                    {(['wearing', 'holding', 'using'] as const).map((mr) => {
                                      const on = rec.model_relation === mr;
                                      const onCls = mr === 'wearing'
                                        ? 'bg-emerald-500 text-white hover:bg-emerald-600'
                                        : mr === 'holding'
                                          ? 'bg-blue-500 text-white hover:bg-blue-600'
                                          : 'bg-violet-500 text-white hover:bg-violet-600';
                                      const hoverCls = mr === 'wearing'
                                        ? 'hover:bg-emerald-100 hover:text-emerald-800'
                                        : mr === 'holding'
                                          ? 'hover:bg-blue-100 hover:text-blue-800'
                                          : 'hover:bg-violet-100 hover:text-violet-800';
                                      return (
                                        <button
                                          key={mr}
                                          type="button"
                                          onClick={() =>
                                            void setRouting(rec, { model_relation: on ? null : mr })
                                          }
                                          className={`px-2 py-0.5 rounded text-xs font-semibold capitalize transition-colors ${
                                            on
                                              ? onCls
                                              : `bg-surface text-dark-muted/70 ${hoverCls}`
                                          }`}
                                          title={`Mark the model as ${mr} the product`}
                                        >
                                          {mr}
                                        </button>
                                      );
                                    })}
                                  </>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </>
            );
          })()}
        </section>
      </div>

      {/* ─── Recipe editor modal (Jun 17 evening v3) ──────────────────
          Walks the recipe object and renders every string-valued field
          as a labeled textarea, grouped by parent key. Save POSTs the
          edited recipe back to the sidecar. Use this to trim things
          like "model wears tortoiseshell glasses" or "props include
          sliced citrus" without re-running the full vision → render →
          approve loop. */}
      {recipeEditTarget && recipeEditDraft && (
        <div
          className="fixed inset-0 z-50 bg-dark/60 backdrop-blur-sm p-4 sm:p-6"
          onClick={closeRecipeEditor}
        >
          <div
            className="relative bg-white rounded-2xl shadow-2xl w-full h-full overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header — full-width, larger type */}
            <header className="px-6 py-4 border-b border-surface-dark/40 flex items-center justify-between flex-shrink-0">
              <div className="min-w-0">
                <h2 className="font-bold text-lg text-dark">Edit recipe</h2>
                <p className="text-sm text-dark-muted mt-0.5 truncate">
                  <span className="font-semibold">{recipeEditTarget.vibe}</span>
                  <span className="mx-2 text-dark-muted/50">·</span>
                  <span className="font-mono">{recipeEditTarget.vibe_slug}/{recipeEditTarget.id}</span>
                  {recipeEditTarget.ready_for_production && (
                    <span className="ml-3 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 text-xs font-semibold border border-emerald-200">
                      ✓ Approved
                    </span>
                  )}
                  {recipeEditTarget.is_product_shot && (
                    <span className="ml-1.5 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-500 text-white text-xs font-bold">
                      📦 Product shot
                    </span>
                  )}
                  {recipeEditTarget.is_infographic && (
                    <span className="ml-1.5 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-fuchsia-500 text-white text-xs font-bold">
                      📊 Infographic
                    </span>
                  )}
                </p>
              </div>
              <button
                type="button"
                onClick={closeRecipeEditor}
                className="text-dark-muted hover:text-dark text-2xl leading-none w-10 h-10 rounded-full hover:bg-surface flex items-center justify-center flex-shrink-0 ml-4"
                aria-label="Close"
              >
                ×
              </button>
            </header>

            {/* Two-column body — left: image + meta. Right: editor (scrolls). */}
            <div className="flex-1 grid grid-cols-1 lg:grid-cols-[minmax(0,420px)_minmax(0,1fr)] gap-0 min-h-0">
              {/* LEFT — moodboard image + read-only metadata + preview render
                  if one exists. Sticky inside its column so the curator can
                  always see what they're editing against. */}
              <aside className="bg-surface/40 border-r border-surface-dark/30 overflow-y-auto p-5 space-y-4">
                <div>
                  <div className="text-[10px] font-semibold uppercase tracking-wide text-dark-muted mb-1.5">
                    Moodboard reference
                  </div>
                  <div className="rounded-lg overflow-hidden border border-surface-dark/40 bg-white">
                    <img
                      src={recipeEditTarget.image_url}
                      alt={recipeEditTarget.id}
                      className="w-full h-auto object-contain"
                    />
                  </div>
                </div>
                {recipeEditTarget.preview_url && (
                  <div>
                    <div className="text-[10px] font-semibold uppercase tracking-wide text-dark-muted mb-1.5">
                      Latest test render
                    </div>
                    <div className="rounded-lg overflow-hidden border border-surface-dark/40 bg-white">
                      <img
                        src={recipeEditTarget.preview_url}
                        alt="latest render"
                        className="w-full h-auto object-contain"
                      />
                    </div>
                  </div>
                )}
                {recipeEditTarget.style_summary && (
                  <div>
                    <div className="text-[10px] font-semibold uppercase tracking-wide text-dark-muted mb-1.5">
                      Style summary
                    </div>
                    <p className="text-sm text-dark leading-relaxed">
                      {recipeEditTarget.style_summary}
                    </p>
                  </div>
                )}
                {(recipeEditTarget.product_types ?? []).length > 0 && (
                  <div>
                    <div className="text-[10px] font-semibold uppercase tracking-wide text-dark-muted mb-1.5">
                      Product types
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {(recipeEditTarget.product_types ?? []).map((t) => (
                        <span
                          key={t}
                          className="px-2 py-0.5 rounded-md bg-white border border-surface-dark/40 text-xs font-semibold uppercase tracking-wide text-dark"
                        >
                          {PRODUCT_TYPE_LABEL[t as ProductType] ?? t}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {(recipeEditTarget.tags ?? []).length > 0 && (
                  <div>
                    <div className="text-[10px] font-semibold uppercase tracking-wide text-dark-muted mb-1.5">
                      Tags
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {(recipeEditTarget.tags ?? []).map((t) => (
                        <span
                          key={t}
                          className="px-2 py-0.5 rounded-md bg-surface text-xs text-dark-muted"
                        >
                          {t}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Routing controls (Jun 23) — moved off the tile so the
                    grid stays clean. Lives in the modal because tagging is
                    a deliberate curator decision, not a quick hover-toggle. */}
                <div className="pt-3 border-t border-surface-dark/40 space-y-3">
                  <div className="text-[10px] font-semibold uppercase tracking-wide text-dark-muted">
                    Product types · click to toggle
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {PRODUCT_TYPES.map((t) => {
                      const on = (recipeEditTarget.product_types ?? []).includes(t);
                      return (
                        <button
                          key={t}
                          type="button"
                          onClick={() => void toggleProductType(recipeEditTarget, t)}
                          className={`px-2 py-1 rounded-md text-[11px] font-semibold uppercase tracking-wide transition-colors ${
                            on
                              ? 'bg-primary text-white'
                              : 'bg-white border border-surface-dark/40 text-dark-muted hover:bg-surface'
                          }`}
                          title={`${on ? 'Remove' : 'Add'} ${PRODUCT_TYPE_LABEL[t]} tag`}
                        >
                          {PRODUCT_TYPE_LABEL[t]}
                        </button>
                      );
                    })}
                    {LEGACY_PRODUCT_TYPES.map((t) => {
                      const on = (recipeEditTarget.product_types ?? []).includes(t);
                      if (!on) return null;
                      return (
                        <button
                          key={t}
                          type="button"
                          onClick={() => void toggleProductType(recipeEditTarget, t)}
                          className="px-2 py-1 rounded-md bg-amber-200 text-amber-900 text-[11px] font-semibold uppercase tracking-wide hover:bg-amber-300 transition-colors"
                          title={`Remove legacy ${PRODUCT_TYPE_LABEL[t]} tag — superseded by apparel / footwear / accessory`}
                        >
                          {PRODUCT_TYPE_LABEL[t]} ✕
                        </button>
                      );
                    })}
                  </div>

                  {recipeEditTarget.requires_model && (
                    <div>
                      <div className="text-[10px] font-semibold uppercase tracking-wide text-dark-muted mb-1.5">
                        Model relation
                      </div>
                      <div className="flex flex-wrap gap-1.5">
                        {(['wearing', 'holding', 'using'] as const).map((mr) => {
                          const on = recipeEditTarget.model_relation === mr;
                          const onCls = mr === 'wearing'
                            ? 'bg-emerald-500 text-white'
                            : mr === 'holding'
                              ? 'bg-blue-500 text-white'
                              : 'bg-violet-500 text-white';
                          return (
                            <button
                              key={mr}
                              type="button"
                              onClick={() =>
                                void setRouting(recipeEditTarget, { model_relation: on ? null : mr })
                              }
                              className={`px-2 py-1 rounded-md text-[11px] font-semibold capitalize transition-colors ${
                                on
                                  ? onCls
                                  : 'bg-white border border-surface-dark/40 text-dark-muted hover:bg-surface'
                              }`}
                              title={`Mark the model as ${mr} the product`}
                            >
                              {mr}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  <div>
                    <div className="text-[10px] font-semibold uppercase tracking-wide text-dark-muted mb-1.5">
                      Render routing
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      <button
                        type="button"
                        onClick={() =>
                          void setRouting(recipeEditTarget, { is_product_shot: !recipeEditTarget.is_product_shot })
                        }
                        className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-semibold uppercase tracking-wide transition-colors ${
                          recipeEditTarget.is_product_shot
                            ? 'bg-emerald-500 text-white'
                            : 'bg-white border border-surface-dark/40 text-dark-muted hover:bg-surface'
                        }`}
                        title={
                          recipeEditTarget.is_product_shot
                            ? 'Text-free product shot — rendered verbatim. Click to unmark.'
                            : 'Mark as a text-free product shot (no copy baked into the recipe).'
                        }
                      >
                        <span>📦</span>
                        {recipeEditTarget.is_product_shot ? '✓ Product shot' : 'Product shot'}
                      </button>
                      <button
                        type="button"
                        onClick={() =>
                          void setRouting(recipeEditTarget, { is_infographic: !recipeEditTarget.is_infographic })
                        }
                        className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-semibold uppercase tracking-wide transition-colors ${
                          recipeEditTarget.is_infographic
                            ? 'bg-fuchsia-500 text-white'
                            : 'bg-white border border-surface-dark/40 text-dark-muted hover:bg-surface'
                        }`}
                        title={
                          recipeEditTarget.is_infographic
                            ? 'Infographic recipe — rendered verbatim. Click to unmark.'
                            : 'Mark as an infographic recipe.'
                        }
                      >
                        <span>📊</span>
                        {recipeEditTarget.is_infographic ? '✓ Infographic' : 'Infographic'}
                      </button>
                    </div>
                  </div>
                </div>
              </aside>

              {/* RIGHT — editable fields + history + test render */}
              <div className="overflow-y-auto px-6 py-5 space-y-5">
              {/* History panel — only when there's something to revert to */}
              {(() => {
                const orig = recipeEditTarget?.original_approved_recipe;
                const history = recipeEditTarget?.approved_recipe_history ?? [];
                if (!orig && history.length === 0) return null;
                return (
                  <details className="rounded-lg border border-surface-dark/40 bg-surface/40">
                    <summary className="cursor-pointer px-3 py-2 text-xs font-semibold uppercase tracking-wide text-dark-muted hover:text-dark">
                      Edit history ({history.length + (orig ? 1 : 0)} version{(history.length + (orig ? 1 : 0)) === 1 ? '' : 's'})
                    </summary>
                    <div className="px-3 py-2 space-y-1.5">
                      {orig && (
                        <div className="flex items-center justify-between gap-2 py-1.5 px-2 rounded-md bg-emerald-50/60 border border-emerald-200/60">
                          <div className="text-xs">
                            <span className="font-semibold text-emerald-900">Original</span>
                            <span className="text-dark-muted ml-1.5">(first approval, immutable baseline)</span>
                          </div>
                          <button
                            type="button"
                            onClick={() => {
                              if (!window.confirm('Replace current draft with the original recipe? You can still cancel before saving.')) return;
                              setRecipeEditDraft(JSON.parse(JSON.stringify(orig)));
                            }}
                            className="px-2 py-1 rounded text-[11px] font-semibold text-emerald-900 hover:bg-emerald-100"
                          >
                            Revert to original
                          </button>
                        </div>
                      )}
                      {[...history].reverse().map((snap, idx) => {
                        const when = (() => {
                          try {
                            return new Date(snap.edited_at).toLocaleString();
                          } catch { return snap.edited_at; }
                        })();
                        return (
                          <div key={idx} className="flex items-center justify-between gap-2 py-1.5 px-2 rounded-md hover:bg-surface">
                            <div className="text-xs text-dark-muted">
                              <span className="font-semibold text-dark">Snapshot</span> · {when}
                            </div>
                            <button
                              type="button"
                              onClick={() => {
                                if (!window.confirm(`Replace current draft with this snapshot? Saved on ${when}. You can still cancel before saving.`)) return;
                                setRecipeEditDraft(JSON.parse(JSON.stringify(snap.recipe)));
                              }}
                              className="px-2 py-1 rounded text-[11px] font-semibold text-primary hover:bg-primary-light"
                            >
                              Revert to this
                            </button>
                          </div>
                        );
                      })}
                      <p className="text-[10px] text-dark-muted/70 pt-2 px-1 leading-relaxed">
                        Revert populates the editor with that version — you still need to click Save to commit. Each Save creates a new snapshot of the pre-edit recipe, so reverts are themselves reversible. History is capped at 10 entries (FIFO).
                      </p>
                    </div>
                  </details>
                );
              })()}

              {/* Recursive walk of the recipe — every string leaf becomes
                  a labeled textarea grouped by parent key. */}
              <RecipeFieldsEditor
                recipe={recipeEditDraft}
                onChange={setDraftAtPath}
              />

              {/* Test render (Jun 17 evening v5). Renders ONE image
                  using the DRAFT (un-saved) recipe + a user-supplied
                  product. Runs the same production render path
                  (render_from_approved_recipe) so output mirrors
                  brand-setup. Iterate edits → Test → Save when happy. */}
              <details className="rounded-lg border border-primary/30 bg-primary-light/40" open>
                <summary className="cursor-pointer px-3 py-2 text-xs font-semibold uppercase tracking-wide text-primary hover:bg-primary-light/60">
                  Test render with this recipe
                </summary>
                <div className="px-3 py-3 space-y-3">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <label className="text-xs">
                      <span className="block text-dark-muted mb-1">Product image *</span>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => setTestProductFile(e.target.files?.[0] ?? null)}
                        className="block w-full text-xs file:mr-2 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:bg-primary file:text-white file:font-medium hover:file:bg-primary-hover"
                      />
                    </label>
                    <label className="text-xs">
                      <span className="block text-dark-muted mb-1">Product name</span>
                      <input
                        type="text"
                        placeholder="cashmere t-shirt"
                        value={testProductName}
                        onChange={(e) => setTestProductName(e.target.value)}
                        className="block w-full px-2 py-1.5 rounded-md border border-surface-dark/50 text-xs"
                      />
                    </label>
                    <label className="text-xs sm:col-span-2">
                      <span className="block text-dark-muted mb-1">
                        Brand name <span className="text-dark-muted/70">(used by no-brand-hallucination block — pass the real brand)</span>
                      </span>
                      <input
                        type="text"
                        placeholder="Shop Rescue Spa"
                        value={testBrandName}
                        onChange={(e) => setTestBrandName(e.target.value)}
                        className="block w-full px-2 py-1.5 rounded-md border border-surface-dark/50 text-xs"
                      />
                    </label>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => void runTestRender()}
                      disabled={testRendering || !testProductFile}
                      className="px-3 py-1.5 rounded-full text-xs font-semibold bg-primary text-white hover:bg-primary-hover disabled:opacity-50"
                    >
                      {testRendering ? 'Rendering… (~25s)' : 'Test render'}
                    </button>
                    {testRenderMeta && (
                      <span className="text-[11px] text-dark-muted">
                        ~${testRenderMeta.cost.toFixed(2)} · {testRenderMeta.latency_s}s
                      </span>
                    )}
                  </div>
                  {testRenderError && (
                    <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-md p-2">
                      {testRenderError}
                    </div>
                  )}
                  {testRenderUrl && (
                    <div className="rounded-md overflow-hidden border border-surface-dark/40 bg-dark/5">
                      <img src={testRenderUrl} alt="Test render" className="w-full h-auto" />
                    </div>
                  )}
                  <p className="text-[10px] text-dark-muted/70 leading-relaxed">
                    Runs the production render path (palette harmonization + kind scaffold + no-brand block + no logo) using the CURRENT draft — does not save. Edit above, click Test, see the result, repeat. Click Save changes when happy.
                  </p>
                </div>
              </details>
              </div>
            </div>

            {/* Footer */}
            <footer className="px-6 py-4 border-t border-surface-dark/40 flex items-center justify-between flex-shrink-0">
              <p className="text-xs text-dark-muted">
                Only string fields are editable. Numbers, arrays, and the
                vibe key are preserved as-is. Save invalidates the picker
                cache — next render uses the new spec.
              </p>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={closeRecipeEditor}
                  className="px-5 py-2 rounded-full text-sm text-dark-muted hover:text-dark hover:bg-surface"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => void saveRecipeEdit()}
                  disabled={recipeEditSaving}
                  className="px-5 py-2 rounded-full text-sm font-medium bg-primary text-white hover:bg-primary-hover disabled:opacity-50"
                >
                  {recipeEditSaving ? 'Saving…' : 'Save changes'}
                </button>
              </div>
            </footer>
          </div>
        </div>
      )}

      {/* Lightbox — SOURCE | RENDER full-screen side-by-side (Jul 7 demo) */}
      {lightbox && (
        <div
          className="fixed inset-0 z-[100] bg-black/90 flex flex-col"
          onClick={() => setLightbox(null)}
        >
          <div className="flex items-center justify-between px-5 py-3 text-white shrink-0">
            <div className="text-sm font-mono opacity-80">
              {lightbox.vibe} · {lightbox.id}
            </div>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); setLightbox(null); }}
              className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 text-white text-lg flex items-center justify-center"
              aria-label="Close"
            >
              ✕
            </button>
          </div>
          <div
            className="flex-1 min-h-0 grid grid-cols-2 gap-2 px-4 pb-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="relative flex items-center justify-center bg-white/5 rounded-xl overflow-hidden">
              <img src={lightbox.image_url} alt="source" className="max-w-full max-h-full object-contain" />
              <span className="absolute bottom-2 left-2 px-2 py-0.5 rounded text-xs font-bold bg-black/70 text-white">SOURCE</span>
            </div>
            <div className="relative flex items-center justify-center bg-white/5 rounded-xl overflow-hidden">
              {lightbox.preview_url ? (
                <img src={lightbox.preview_url} alt="render" className="max-w-full max-h-full object-contain" />
              ) : (
                <div className="text-white/50 text-sm">No render preview</div>
              )}
              <span className="absolute bottom-2 left-2 px-2 py-0.5 rounded text-xs font-bold bg-primary text-white">RENDER</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Recipe field renderer (recursive) ───────────────────────────────
// Walks the recipe object. For every leaf that's a string → renders a
// labeled textarea. For nested objects → renders a labeled subsection
// with indentation. Arrays + numbers + booleans render as read-only
// fields (uncommon edit targets; keep the UI clean).
function RecipeFieldsEditor({
  recipe,
  onChange,
  pathPrefix = '',
  depth = 0,
}: {
  recipe: Record<string, unknown>;
  onChange: (path: string, value: string) => void;
  pathPrefix?: string;
  depth?: number;
}) {
  const entries = Object.entries(recipe);
  return (
    <div className={depth === 0 ? 'space-y-5' : 'space-y-3 ml-3 pl-3 border-l border-surface-dark/40'}>
      {entries.map(([key, value]) => {
        const fullPath = pathPrefix ? `${pathPrefix}.${key}` : key;
        const label = key.replace(/_/g, ' ');

        if (typeof value === 'string') {
          const isLong = value.length > 80 || value.includes('\n');
          return (
            <label key={fullPath} className="block">
              <span className="block text-xs font-semibold uppercase tracking-wide text-dark-muted mb-1.5">
                {label}
              </span>
              {isLong ? (
                <textarea
                  value={value}
                  onChange={(e) => onChange(fullPath, e.target.value)}
                  className="w-full px-3 py-2.5 rounded-lg border border-surface-dark/50 text-base font-mono leading-relaxed focus:border-primary focus:outline-none"
                  rows={Math.min(10, Math.max(3, Math.ceil(value.length / 70)))}
                />
              ) : (
                <input
                  type="text"
                  value={value}
                  onChange={(e) => onChange(fullPath, e.target.value)}
                  className="w-full px-3 py-2.5 rounded-lg border border-surface-dark/50 text-base focus:border-primary focus:outline-none"
                />
              )}
            </label>
          );
        }

        if (value && typeof value === 'object' && !Array.isArray(value)) {
          return (
            <div key={fullPath}>
              <h3 className={`font-semibold text-dark mb-2 ${depth === 0 ? 'text-sm uppercase tracking-wide' : 'text-xs'}`}>
                {label}
              </h3>
              <RecipeFieldsEditor
                recipe={value as Record<string, unknown>}
                onChange={onChange}
                pathPrefix={fullPath}
                depth={depth + 1}
              />
            </div>
          );
        }

        // Array / number / boolean / null — read-only display so the
        // curator can SEE the field exists but can't accidentally
        // mangle it via free-text input.
        return (
          <div key={fullPath} className="text-xs">
            <span className="font-semibold uppercase tracking-wide text-dark-muted">{label}: </span>
            <span className="text-dark-muted/70 font-mono">
              {Array.isArray(value)
                ? `[${value.length} items — edit via JSON / GCS console]`
                : String(value)}
            </span>
          </div>
        );
      })}
    </div>
  );
}


// ─── FilterChip helper (Jun 23) ──────────────────────────────────────
// Mirrors /moodboard-previews. Tone variants: 'default' (primary on/off)
// and 'amber' (used by ⚠ Untagged to signal the curator queue).
function FilterChip({
  label,
  active,
  onClick,
  tone = 'default',
}: {
  label: string;
  active: boolean;
  onClick: () => void;
  tone?: 'default' | 'amber' | 'violet';
}) {
  const activeCls = tone === 'amber'
    ? 'bg-amber-500 text-white'
    : tone === 'violet'
    ? 'bg-violet-500 text-white'
    : 'bg-primary text-white';
  const restCls = tone === 'amber'
    ? 'bg-amber-50 text-amber-700 border border-amber-200 hover:bg-amber-100'
    : tone === 'violet'
    ? 'bg-violet-50 text-violet-700 border border-violet-200 hover:bg-violet-100'
    : 'bg-surface text-dark-muted hover:bg-surface-dark/40 hover:text-dark';
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-2.5 py-1 rounded-full text-[11px] font-semibold transition-colors ${
        active ? activeCls : restCls
      }`}
    >{label}</button>
  );
}
